/// <amd-module name="Dashboards/DashboardView/src/redux/actions/Actions" />
import { ActionsUnion, createAction } from "redux/Utils/ActionHelper";

import { Dashboard } from "../../model/Dashboard";
import { DashboardPermissionsState } from "../../model/DashboardViewState";
import { BoundingRectangle, Viewport } from "../../model/Viewport";
import { Widget } from "../../model/Widget";
import { WidgetMap } from "../../model/WidgetMap";
import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import * as DashboardPanelConstants from "Dashboards/DashboardPanel/Common";
import { GridItemPosition } from "Dashboards/Grid/DashboardGridItem";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetCopyProps, WidgetHostLoadedEventInfo } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";

import { IIdentity } from "VSS/Features/Identities/IdentityService";

import { ITeamData } from "Tfs/Platform/Page";

export const enum DashboardActionTypes {
    SetDashboard = "DashboardView/SetDashboard",
    SetPastWidgets = "DashboardView/SetPastWidgets",
    UpdateWidgetEtag = "DashboardView/UpdateWidgetEtag",
    SetWidgets = "DashboardView/SetWidgets",
    SetDashboardAndWidgets = "DashboardView/SetDashboardAndWidgets",

    UnloadDashboard = "DashboardView/UnsetDashboard",
    RefreshDashboard = "DashboardView/RefreshDashboard",
    SaveDashboard = "DashboardView/SaveDashboard",

    // Driven by events from Widget Config Panel
    SavePreviewWidgetConfig = "DashboardView/SavePreviewWidgetConfig",
    UpdatePreviewWidgetConfig = "DashboardView/UpdatePreviewWidgetConfig",
    TogglePreviewHighlighting = "DashboardView/TogglePreviewHighlighting",

    AddWidgetFromCatalog = "DashboardView/AddWidgetFromCatalog",
    DropWidgetFromCatalog = "DashboardView/DropWidgetFromCatalog",
    NotifyWidgetsMoved = "DashboardView/WidgetsMoved",

    UpdateWidgetsSeen = "DashboardView/UpdateWidgetsSeen",
    UpdateDashboardSettings = "DashboardView/UpdateDashboardSettings",

    UpdateRefreshTimer = "DashboardView/UpdateRefreshTimer",
    ToggleRefreshTimeDisplay = "DashboardView/ToggleRefreshTimeDisplay",
    StartAutoRefreshTimer = "DashboardView/StartAutoRefreshTimer",
    StopAutoRefreshTimer = "DashboardView/StopAutoRefreshTimer",

    SetTeamData = "DashboardView/SetTeamData",
    SetOwnerIdentity = "DashboardView/SetOwnerIdentity",
    SetDefaultProjectTeamData = "DashboardView/SetDefaultProjectTeamData",
    SetEmbedded = "DashboardView/SetEmbedded",
    SetPermissions = "DashboardView/SetPermissions",

    ToggleEditMode = "DashboardView/ToggleEditMode",
    hideDashboardTemplatePage = "DashboardView/hideDashboardTemplatePage",
    // Note: Config mode takes precedence over edit mode. After finishing config mode, the view *returns* to the prior state of view or edit mode.
    ToggleConfigMode = "DashboardView/ToggleConfigMode",
    ToggleFullScreen = "DashboardsView/ToggleFullScreen",
    ToggleDashboardSettingsManager = "DashboardView/ToggleDashboardSettingsManager",
    OpenCopyDashboardPanel = "DashboardView/OpenCopyDashboardPanel",
    ToggleCopyDashboardPanel = "DashboardView/ToggleCopyDashboardPanel",
    NavigateToDashboard = "DashboardView/NavigateToDashboard",
    ActivateCopyDashboardPanel = "DashboardView/ActivateCopyDashboardPanel",
    InitializeStateForCopyDashboard = "DashboardView/InitializeStateForCopyDashboard",
    LoadNewTeamsForProject = "DashboardView/LoadNewTeamsForProject",
    PutNewTeamsForProject = "DashboardView/PutNewTeamsForProject",

    NotifyWidgetLoaded = "DashboardView/WidgetLoaded",
    NotifyDashboardInitialized = "DashboardView/DashboardInitialized",

    DeleteWidget = "DashboardView/DeleteWidget",
    CopyWidget = "DashboardView/CopyWidget",
    CopyWidgetToOtherDashboard = "DashboardView/CopyWidgetToOtherDashboard",
    CopyWidgetToThisDashboard = "DashboardView/CopyWidgetToThisDashboard",
    ConfigureWidget = "DashboardView/ConfigureWidget",

    SetThrottlingWarning = "DashboardsView/SetThrottlingWarning",
    SetAllowThrottling = "DashboardsView/SetAllowThrottling",
    DismissThrottlingWarning = "DashboardView/DismissThrottlingWarning",

    SetOmitTenPercentVisibleWidgets = "DashboardView/SetOmitTenPercentVisibleWidgets"
}

export namespace DashboardActions {
    export const setDashboard = (dashboard: Dashboard) => createAction(DashboardActionTypes.SetDashboard, { dashboard });
    export const setWidgets = (widgets: ReadonlyArray<Widget>) => createAction(DashboardActionTypes.SetWidgets, { widgets });
    export const setPastWidgets = (pastWidgets: ReadonlyArray<Widget>) => createAction(DashboardActionTypes.SetPastWidgets, { pastWidgets });
    export const updateWidgetEtag = (widgets: ReadonlyArray<Widget>) => createAction(DashboardActionTypes.UpdateWidgetEtag, { widgets });
    export const setDashboardAndWidgets = (dashboard: Dashboard, widgets: ReadonlyArray<Widget>, pastWidgets: ReadonlyArray<Widget>) =>
        createAction(DashboardActionTypes.SetDashboardAndWidgets, { dashboard, widgets, pastWidgets });

    export const unloadDashboard = () => createAction(DashboardActionTypes.UnloadDashboard);
    export const refreshDashboard = (isAutoRefresh: boolean, isHardRefresh?: boolean) =>
        createAction(DashboardActionTypes.RefreshDashboard, { isAutoRefresh: isAutoRefresh, isHardRefresh: isHardRefresh });
    export const saveDashboard = () => createAction(DashboardActionTypes.SaveDashboard);

    export const notifyWidgetsMoved = (widgetPositions: GridItemPosition[]) =>
        createAction(DashboardActionTypes.NotifyWidgetsMoved, { widgetPositions });

    export const updateWidgetsSeen = (gridViewport: Viewport, gridItemRectangles: WidgetMap<BoundingRectangle>) =>
        createAction(DashboardActionTypes.UpdateWidgetsSeen, { gridViewport, gridItemRectangles });
    export const updateDashboardSettings = (
        dashboardName: string,
        dashboardDescription: string,
        dashboardRefreshInterval: number,
        dashboardOwner: string
    ) =>
        createAction(DashboardActionTypes.UpdateDashboardSettings, { dashboardName, dashboardDescription, dashboardRefreshInterval, dashboardOwner });

    export const updateRefreshTimer = () => createAction(DashboardActionTypes.UpdateRefreshTimer);
    export const toggleRefreshTimeDisplay = (showRefreshTimeDisplay: boolean) =>
        createAction(DashboardActionTypes.ToggleRefreshTimeDisplay, { showRefreshTimeDisplay });
    export const startAutoRefreshTimer = () => createAction(DashboardActionTypes.StartAutoRefreshTimer);
    export const stopAutoRefreshTimer = () => createAction(DashboardActionTypes.StopAutoRefreshTimer);

    export const setTeamData = (teamData: ITeamData) => createAction(DashboardActionTypes.SetTeamData, { teamData });
    export const setOwnerIdentity = (ownerIdentity: IIdentity) => createAction(DashboardActionTypes.SetOwnerIdentity, { ownerIdentity });
    export const setDefaultProjectTeamData = (defaultProjectTeamData: ITeamData) =>
        createAction(DashboardActionTypes.SetDefaultProjectTeamData, { defaultProjectTeamData });
    export const setEmbedded = (isEmbedded: boolean) => createAction(DashboardActionTypes.SetEmbedded, { isEmbedded });
    export const setPermissions = (permissions: DashboardPermissionsState) => createAction(DashboardActionTypes.SetPermissions, { permissions });

    export const showEditMode = () => createAction(DashboardActionTypes.ToggleEditMode, { showEditMode: true });
    export const hideEditMode = () => createAction(DashboardActionTypes.ToggleEditMode, { showEditMode: false });

    export const hideDashboardTemplatePage = () => createAction(DashboardActionTypes.hideDashboardTemplatePage);

    export const showConfigMode = (selectedWidgetId: string) =>
        createAction(DashboardActionTypes.ToggleConfigMode, { showConfigMode: true, selectedWidgetId: selectedWidgetId });
    export const hideConfigMode = () => createAction(DashboardActionTypes.ToggleConfigMode, { showConfigMode: false, selectedWidgetId: undefined });

    export const updatePreviewWidgetConfig = (previewWidgetConfig: DashboardWidgetProps) =>
        createAction(DashboardActionTypes.UpdatePreviewWidgetConfig, { previewWidgetConfig: previewWidgetConfig });
    export const togglePreviewHighlighting = (highlightPreviewConfig: boolean) =>
        createAction(DashboardActionTypes.TogglePreviewHighlighting, { highlightPreviewConfig: highlightPreviewConfig });
    export const savePreviewWidgetConfig = (previewWidgetConfig: DashboardWidgetProps) =>
        createAction(DashboardActionTypes.SavePreviewWidgetConfig, { previewWidgetConfig: previewWidgetConfig });

    export const toggleFullScreen = (showFullScreen: boolean) => createAction(DashboardActionTypes.ToggleFullScreen, { showFullScreen });

    export const showDashboardSettingsManager = () =>
        createAction(DashboardActionTypes.ToggleDashboardSettingsManager, { showDashboardSettingsManager: true });
    export const hideDashboardSettingsManager = () =>
        createAction(DashboardActionTypes.ToggleDashboardSettingsManager, { showDashboardSettingsManager: false });
    
    export const activateCopyDashboardPanel = () =>
        createAction(DashboardActionTypes.ActivateCopyDashboardPanel);
    export const showCopyDashboardPanel = () =>
        createAction(DashboardActionTypes.ToggleCopyDashboardPanel, { showCopyDashboardPanel : true });
    export const hideCopyDashboardPanel = () =>
        createAction(DashboardActionTypes.ToggleCopyDashboardPanel, { showCopyDashboardPanel : false });
    export const navigateToDashboard = (dashboard:Pick<TeamContext & Dashboard, "id" | "teamId" | "projectId" | "dashboardScope">) =>
        createAction(DashboardActionTypes.NavigateToDashboard, { dashboad : dashboard });

    export const notifyWidgetLoaded = (eventInfo: WidgetHostLoadedEventInfo) => createAction(DashboardActionTypes.NotifyWidgetLoaded, { eventInfo });
    export const notifyDashboardInitialized = () => createAction(DashboardActionTypes.NotifyDashboardInitialized);

    export const deleteWidget = (widgetProps: DashboardWidgetProps) => createAction(DashboardActionTypes.DeleteWidget, { widgetProps });
    export const configureWidget = (widgetProps: DashboardWidgetProps) => createAction(DashboardActionTypes.ConfigureWidget, { widgetProps });
    export const copyWidget = (widgetCopyProps: WidgetCopyProps) => createAction(DashboardActionTypes.CopyWidget, { widgetCopyProps });
    export const copyWidgetToOtherDashboard = (widgetCopyProps: WidgetCopyProps) =>
        createAction(DashboardActionTypes.CopyWidgetToOtherDashboard, { widgetCopyProps });
    export const copyWidgetToThisDashboard = (initialWidgetSettings: DashboardWidgetProps) =>
        createAction(DashboardActionTypes.CopyWidgetToThisDashboard, { initialWidgetSettings });
    export const addWidgetFromCatalog = (initialWidgetSettings: DashboardWidgetProps) =>
        createAction(DashboardActionTypes.AddWidgetFromCatalog, { initialWidgetSettings });
    export const dropWidgetFromCatalog = (initialWidgetSettings: DashboardWidgetProps, dragEvent: Event) =>
        createAction(DashboardActionTypes.DropWidgetFromCatalog, { initialWidgetSettings, dragEvent });

    export const setAllowThrottling = (allowThrottling: boolean) => createAction(DashboardActionTypes.SetAllowThrottling, { allowThrottling });
    export const setThrottlingWarning = (showThrottlingWarning: boolean) =>
        createAction(DashboardActionTypes.SetThrottlingWarning, { showThrottlingWarning });
    export const dismissThrottlingMessage = () => createAction(DashboardActionTypes.DismissThrottlingWarning);

    export const setOmitTenPercentVisibleWidgets = (omitTenPercentVisibleWidgetsFromRender: boolean) =>
        createAction(DashboardActionTypes.SetOmitTenPercentVisibleWidgets, { omitTenPercentVisibleWidgetsFromRender });

    export const openCopyDashboardPanel = () => createAction(DashboardActionTypes.OpenCopyDashboardPanel );
    export const initializeStateForCopyDashboard = (useDedicatedApi: boolean, projectList: DashboardPanelConstants.Project[], 
        teamProjectMap: {[id:string]: DashboardPanelConstants.TeamProjectItem}, dashboard: Dashboard ) => createAction(DashboardActionTypes.InitializeStateForCopyDashboard, {
            useDedicatedApi : useDedicatedApi,
            projectList: projectList,
            teamProjectMap: teamProjectMap,
            dashboard: dashboard
        });
    export const loadNewTeamsForProject = (project: DashboardPanelConstants.Project) => createAction(DashboardActionTypes.LoadNewTeamsForProject, { project: project }); 
    export const putNewTeamsForProject = (projectId: string, teamProjectItem: DashboardPanelConstants.TeamProjectItem) => createAction(DashboardActionTypes.PutNewTeamsForProject, { projectId: projectId, teamProjectItem: teamProjectItem }); 
}

export type DashboardAction = ActionsUnion<typeof DashboardActions>;
